import React, { useEffect, useMemo, useState } from "react";
import TableGeneric from "../../../../shared/components/generic/table/table-generic";
import FormGeneric from "../../../../shared/components/generic/form/form-generic";
import AdminSection from "../../components/admin-section/admin-section";
import { netSalesColumnsConfig } from "../../config/net-sales/net-sales-columns-config";
import { showConfirmAlert } from "../../../../shared/components/generic/alert/confirm-alert/confirm-alert";
import { netSalesFormConfig } from "../../config/net-sales/net-sales-form-config";
import FormModal from "../../../../shared/components/generic/form/form-modal/form-modal";
import { useAdminFormModal } from "../../hooks/useAdminFormModal";
import { confirmSaveChanges, showNoRecordsFoundAlert } from "../../utils/admin-utils";
import { showCommonAlert } from "../../../../shared/components/generic/alert/utils/show-common-alert-utils";
import Pagination from "../../../../shared/components/generic/pagination/pagination";
import { buildSegmentationOptions } from "../../utils/segmentation-utils";
import { useSegmentations } from "../../hooks/segmentacao/useSegmentations";
import { useNetSales } from "../../hooks/net-sales/useNetSales";
import { useNetSalesNew } from "../../hooks/net-sales/useNetSalesNew";
import { useNetSalesUpdate } from "../../hooks/net-sales/useNetSalesUpdate";
import { useNetSalesDelete } from "../../hooks/net-sales/useNetSalesDelete";
import { usePactoRebatesParams } from "../../hooks/parametros-pacto-rebates/usePactoRebatesParams";
import {
	hasValidationErrors,
	removeClearedValidationErrors
} from "../../../utils/validation-utils";
import PageLoading from "../../../../shared/components/generic/page-loading/page-loading";
import MonetaryUtils from "../../../../shared/utils/MonetaryUtils";

const SEGMENTATIONS_SELECT_PAGE_SIZE = 9999;

const isConfirmAccepted = result => {
	return result?.isConfirmed === true || result?.value === true;
};

const buildNetSalesFilterConfig = ({
	segmentations = [],
	loadingSegmentations = false,
	selectedSegmentation = ""
}) => {
	const segmentationOptions = buildSegmentationOptions(segmentations);

	return [
		{
			fields: [
				{
					name: "segmentation",
					type: "select",
					placeholder: loadingSegmentations ? "Carregando..." : "Todos",
					options: segmentationOptions,
					defaultValue: selectedSegmentation,
					disabled: loadingSegmentations,
					columns: 12
				}
			]
		}
	];
};

const ObjetivosNetSales = () => {
	const netSalesModal = useAdminFormModal();

	const [segmentationsForSelect, setSegmentationsForSelect] = useState([]);
	const [selectedSegmentationFilter, setSelectedSegmentationFilter] = useState("");
	const [netSalesActionLoadingMessage, setNetSalesActionLoadingMessage] = useState("");
	const [netSalesValidationErrors, setNetSalesValidationErrors] = useState({});
	const [clearedValidationErrors, setClearedValidationErrors] = useState({});
	const [netSalesPagination, setNetSalesPagination] = useState({
		totalElements: 0,
		totalPages: 0,
		page: 0,
		size: 10
	});
	const [pendingNoRecordsAlert, setPendingNoRecordsAlert] = useState(false);

	const {
		netSales,
		loading: netSalesLoading,
		refetch: fetchNetSales
	} = useNetSales();

	const {
		loading: createNetSalesLoading,
		validateNetSales,
		createNetSales
	} = useNetSalesNew();

	const {
		loading: updateNetSalesLoading,
		updateNetSales
	} = useNetSalesUpdate();

	const {
		loading: deleteNetSalesLoading,
		deleteNetSales
	} = useNetSalesDelete();

	const {
		loading: segmentationsLoading,
		refetch: fetchSegmentations
	} = useSegmentations();

	const {
		pactoRebatesParams,
		loading: pactoRebatesParamsLoading,
		refetch: fetchPactoRebatesParams
	} = usePactoRebatesParams();

	const currentPactoRebatesParams = pactoRebatesParams?.[0] || null;

	const baseInstallments =
		currentPactoRebatesParams?.numberOfInstallmentsForCalculationBase ?? "-";

	const isLoadingNetSalesPage =
		netSalesLoading ||
		segmentationsLoading ||
		pactoRebatesParamsLoading;

	const isSavingNetSales =
		createNetSalesLoading ||
		updateNetSalesLoading;

	const isDeletingNetSales = deleteNetSalesLoading;

	const loadNetSales = async (
		pagination = netSalesPagination,
		segmentationFilter = selectedSegmentationFilter,
		{ showEmptyAlert = true } = {}
	) => {
		const result = await fetchNetSales(
			{
				page: pagination.page,
				size: pagination.size
			},
			{
				idSegmentacao: segmentationFilter
			}
		);

		if (!result.ok) {
			await showCommonAlert({
				title: "Erro",
				message: result.error,
				fallbackMessage: "Não foi possível carregar as metas Net Sales.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return result;
		}

		setNetSalesPagination(result.pagination);

		if (result.empty && showEmptyAlert) {
			setPendingNoRecordsAlert(true);
		}

		return result;
	};

	const loadSegmentationsForSelect = async () => {
		const result = await fetchSegmentations({
			page: 0,
			size: SEGMENTATIONS_SELECT_PAGE_SIZE
		});

		if (!result.ok) {
			setSegmentationsForSelect([]);

			await showCommonAlert({
				title: "Erro",
				message: result.error,
				fallbackMessage: "Não foi possível carregar os tamanhos de cliente.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		setSegmentationsForSelect(result.segmentations || []);
	};

	const PACTO_REBATES_NET_SALES_FIELDS = [
		"quantidadePadraoParcelasBaseCalculo"
	];

	const loadPactoRebatesParams = async () => {
		const result = await fetchPactoRebatesParams({
			fields: PACTO_REBATES_NET_SALES_FIELDS
		});

		if (!result.ok) {
			await showCommonAlert({
				title: "Erro",
				message: result.error,
				fallbackMessage: "Não foi possível carregar os parâmetros do Pacto Rebates.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});
		}
	};
	useEffect(() => {
		loadNetSales({
			page: 0,
			size: netSalesPagination.size
		});

		loadSegmentationsForSelect();
		loadPactoRebatesParams();
	}, []);

	useEffect(() => {
		if (!pendingNoRecordsAlert) {
			return;
		}

		if (isLoadingNetSalesPage || netSalesActionLoadingMessage) {
			return;
		}

		const timeoutId = setTimeout(() => {
			setPendingNoRecordsAlert(false);
			showNoRecordsFoundAlert();
		}, 0);

		return () => clearTimeout(timeoutId);
	}, [
		pendingNoRecordsAlert,
		isLoadingNetSalesPage,
		netSalesActionLoadingMessage
	]);

	const visibleNetSalesValidationErrors = removeClearedValidationErrors({
		errors: netSalesValidationErrors,
		clearedErrors: clearedValidationErrors
	});

	const getSegmentationNameById = segmentationId => {
		const selectedSegmentation = segmentationsForSelect.find(segmentation => {
			return String(segmentation.id) === String(segmentationId);
		});

		return selectedSegmentation?.segmentation;
	};

	const buildNetSalesValuesWithSegmentationName = values => {
		const segmentationId = values.segmentation;

		return {
			...values,
			segmentation: segmentationId,
			segmentationName: getSegmentationNameById(segmentationId)
		};
	};

	const handlePageChange = ({ selected }) => {
		loadNetSales({
			...netSalesPagination,
			page: selected
		});
	};

	const handleNetSalesFilterChange = ({ values }) => {
		const nextSegmentationFilter = values.segmentation || "";

		setSelectedSegmentationFilter(nextSegmentationFilter);

		loadNetSales(
			{
				page: 0,
				size: netSalesPagination.size
			},
			nextSegmentationFilter
		);
	};

	const handleOpenCreate = () => {
		setNetSalesValidationErrors({});
		setClearedValidationErrors({});
		netSalesModal.openCreate();
	};

	const handleEdit = row => {
		setNetSalesValidationErrors({});
		setClearedValidationErrors({});
		netSalesModal.openEdit(row);
	};

	const handleCloseNetSalesModal = () => {
		setNetSalesValidationErrors({});
		setClearedValidationErrors({});
		netSalesModal.close();
	};

	const handleNetSalesFieldChange = ({ name }) => {
		setClearedValidationErrors(previous => ({
			...previous,
			[name]: true
		}));
	};

	const handleSubmitNetSales = async values => {
		const selectedItem = netSalesModal.selectedItem;
		const isEditing = netSalesModal.isEditing;
		const valuesToSave = buildNetSalesValuesWithSegmentationName(values);

		const validationErrors = validateNetSales(valuesToSave);

		if (hasValidationErrors(validationErrors)) {
			setClearedValidationErrors({});
			setNetSalesValidationErrors(validationErrors);

			await showCommonAlert({
				title: "Atenção",
				html: Object.values(validationErrors).join("<br />"),
				icon: "warning",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		setNetSalesValidationErrors({});
		setClearedValidationErrors({});
		netSalesModal.close();

		const confirmResult = await confirmSaveChanges({
			description: "Deseja salvar as alterações?"
		});

		if (!isConfirmAccepted(confirmResult)) {
			return;
		}

		let saveResult;
		let reloadResult;

		setNetSalesActionLoadingMessage("Salvando meta Net Sales...");

		try {
			saveResult = isEditing
				? await updateNetSales(
					valuesToSave.segmentation,
					selectedItem.id,
					valuesToSave
				)
				: await createNetSales(
					valuesToSave.segmentation,
					valuesToSave
				);

			if (saveResult.ok) {
				if (isEditing) {
					reloadResult = await loadNetSales(
						{
							page: netSalesPagination.page,
							size: netSalesPagination.size
						},
						selectedSegmentationFilter,
						{
							showEmptyAlert: false
						}
					);
				} else {
					setSelectedSegmentationFilter("");

					reloadResult = await loadNetSales(
						{
							page: 0,
							size: netSalesPagination.size
						},
						"",
						{
							showEmptyAlert: false
						}
					);
				}
			}
		} finally {
			setNetSalesActionLoadingMessage("");
		}

		if (!saveResult.ok) {
			await showCommonAlert({
				title: "Erro",
				message: saveResult.error,
				fallbackMessage: "Não foi possível salvar a meta Net Sales.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		if (reloadResult?.empty) {
			await showNoRecordsFoundAlert();
			return;
		}

		await showCommonAlert({
			title: "Sucesso",
			message: isEditing
				? "Meta Net Sales atualizada com sucesso."
				: "Meta Net Sales incluída com sucesso.",
			icon: "success",
			customClass: {
				container: "common-alert__container",
				confirmButton: "alert-form-button"
			}
		});
	};

	const handleDelete = row => {
		showConfirmAlert({
			title: "Exclusão",
			description: (
				<>
					Tem certeza que deseja excluir a meta de crescimento acima de {MonetaryUtils.formatPercentage(row.growth)}{" "}
					para o cliente de tamanho {row.segmentation}?
					<br />
					<strong>Esta exclusão não afetará os contratos já existentes.</strong>
				</>
			),
			confirmLabel: "Sim, excluir",
			cancelLabel: "Cancelar"
		}).then(async result => {
			if (!isConfirmAccepted(result)) {
				return;
			}

			let deleteResult;
			let reloadResult;

			setNetSalesActionLoadingMessage("Excluindo meta Net Sales...");

			try {
				deleteResult = await deleteNetSales(
					row.idSegmentation,
					row.id
				);

				if (deleteResult.ok) {
					reloadResult = await loadNetSales(
						{
							page: netSalesPagination.page,
							size: netSalesPagination.size
						},
						selectedSegmentationFilter,
						{
							showEmptyAlert: false
						}
					);
				}
			} finally {
				setNetSalesActionLoadingMessage("");
			}

			if (!deleteResult.ok) {
				await showCommonAlert({
					title: "Erro",
					message: deleteResult.error,
					fallbackMessage: "Não foi possível excluir a meta Net Sales.",
					icon: "error",
					customClass: {
						container: "common-alert__container",
						confirmButton: "alert-form-button"
					}
				});

				return;
			}

			await showCommonAlert({
				title: "Sucesso",
				message: "Meta Net Sales excluída com sucesso.",
				icon: "success",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			if (reloadResult?.empty) {
				await showNoRecordsFoundAlert();
			}
		});
	};

	const columns = useMemo(() => {
		return netSalesColumnsConfig({
			onEdit: handleEdit,
			onDelete: handleDelete
		});
	}, [
		selectedSegmentationFilter,
		netSalesPagination.page,
		netSalesPagination.size
	]);

	const filterFields = useMemo(() => {
		return buildNetSalesFilterConfig({
			segmentations: segmentationsForSelect,
			loadingSegmentations: segmentationsLoading,
			selectedSegmentation: selectedSegmentationFilter
		});
	}, [
		segmentationsForSelect,
		segmentationsLoading,
		selectedSegmentationFilter
	]);

	const formFields = useMemo(() => {
		return netSalesFormConfig({
			netSales: netSalesModal.selectedItem,
			segmentations: segmentationsForSelect,
			loadingSegmentations: segmentationsLoading,
			isEditing: netSalesModal.isEditing
		});
	}, [
		netSalesModal.selectedItem,
		netSalesModal.isEditing,
		segmentationsForSelect,
		segmentationsLoading
	]);

	return (
		<>
			<PageLoading
				disabled={netSalesModal.isOpen}
				actionMessage={netSalesActionLoadingMessage}
				states={[
					{
						loading: isLoadingNetSalesPage,
						message: "Carregando metas Net Sales..."
					},
					{
						loading: isDeletingNetSales,
						message: "Excluindo meta Net Sales..."
					},
					{
						loading: isSavingNetSales,
						message: "Salvando meta Net Sales..."
					}
				]}
			/>
			<AdminSection
				title="Metas de crescimento de Net Sales"
				actionLabel="Incluir meta"
				actionStartIcon="plusCircle"
				onAction={handleOpenCreate}
			>
				<div className="net-sales__settings">
					<div className="net-sales__client-size-filter">
						<span className="net-sales__client-size-filter-label">
							Filtrar por Tamanho do Cliente:
						</span>

						<div className="net-sales__client-size-filter-form">
							<FormGeneric
								formId="net-sales-filter-form"
								formFields={filterFields}
								message={null}
								onSubmit={() => {}}
								onFieldChange={handleNetSalesFilterChange}
							/>
						</div>
					</div>

					<div className="net-sales__base-installments">
						<div className="net-sales__base-installments-content">
							<span className="net-sales__base-installments-label">
								Quantidade de parcelas base:
							</span>

							<strong className="net-sales__base-installments-value">
								{baseInstallments}
							</strong>
						</div>
					</div>
				</div>

				<TableGeneric
					columns={columns}
					data={netSales}
					rowKey="id"
				/>

				{netSalesPagination.totalPages > 1 && (
					<Pagination
						pageCount={netSalesPagination.totalPages}
						currentPage={netSalesPagination.page}
						onPageChange={handlePageChange}
					/>
				)}
			</AdminSection>

			<FormModal
				open={netSalesModal.isOpen}
				title={netSalesModal.isEditing ? "Editar meta de crescimento" : "Incluir meta de crescimento"}
				formId="net-sales-form"
				formFields={formFields}
				errors={visibleNetSalesValidationErrors}
				onClose={handleCloseNetSalesModal}
				onSubmit={handleSubmitNetSales}
				onFieldChange={handleNetSalesFieldChange}
				submitLabel="Salvar"
				cancelLabel="Cancelar"
				submitDisabled={
					isSavingNetSales ||
					isDeletingNetSales ||
					isLoadingNetSalesPage ||
					Boolean(netSalesActionLoadingMessage)
				}
			/>
		</>
	);
};

export default ObjetivosNetSales;